// src/dataStore.js
// Centralized data store and reverse index utility for keyboard project

import Articles from './articlesData';
import Builds from './buildsData';

// --- Entity Classes ---
class Keyboard {
	constructor(id, name, brand, layout, price, color, articleid) {
		this.id = id;
		this.name = name;
		this.brand = brand;
		this.layout = layout;
		this.price = price;
		this.color = color;
		this.articleid = articleid || null;
	}
}

class Keycap {
	constructor(id, name, manufacturer, price, aliceSupport, numpadSupport) {
		this.id = id;
		this.name = name;
		this.manufacturer = manufacturer;
		this.price = price;
		this.aliceSupport = aliceSupport;
		this.numpadSupport = numpadSupport;
	}
}

class Switch {
	constructor(id, name, type, manufacturer, articleid, visible = true) {
		this.id = id;
		this.name = name;
		this.type = type;
		this.manufacturer = manufacturer;
		this.articleid = articleid || null;
		this.visible = visible;
	}
}

class Plate {
	constructor(id, material) {
		this.id = id;
		this.material = material;
	}
}

class KeyboardBuild {
	constructor(id, name, keyboardId, switchId, keycapId, plateId, options = {}) {
		this.id = id;
		this.name = name;
		this.keyboardId = keyboardId;
		this.switchId = switchId;
		this.keycapId = keycapId;
		this.plateId = plateId;
		this.foam = options.foam || [];
		this.mount = options.mount || '';
        this.audio = options.audio || [];
		// Sound test fields
		this.soundDescriptors = options.soundDescriptors || [];
		this.youtube = options.youtube || [];
		this.hasSoundtest = options.hasSoundtest ?? (this.youtube.length > 0);
		this.thumbnail = options.thumbnail || null;
	}
}

class Article {
	constructor(id, title, type, description, thumbnail, date, status, link = null, visible = true) {
		this.id = id;
		this.title = title;
		this.type = type; // 'keyboard' | 'switch' | 'keycap' | 'misc'
		this.description = description;
		this.thumbnail = thumbnail;
		this.date = date;
		this.status = status; // 'new' | 'updated' | 'outdated'
		this.link = link; // Optional link property for navigation
		this.visible = visible; // Determines if the article is displayed
	}
}

// --- Hashmap Stores ---
export const keyboards = {};
export const keycaps = {};
export const switches = {};
export const builds = {};
export const plates = {};

// --- Reverse Indexes ---
export const buildsBySwitch = {};
export const buildsByKeycap = {};
export const buildsByKeyboard = {};

// --- Utility to Add Build and Update Indexes ---
export function addBuild(build) {
	builds[build.id] = build;
	if(!buildsBySwitch[build.switchId]) buildsBySwitch[build.switchId] = [];
	buildsBySwitch[build.switchId].push(build.id);
	if(!buildsByKeycap[build.keycapId]) buildsByKeycap[build.keycapId] = [];
	buildsByKeycap[build.keycapId].push(build.id);
	if(!buildsByKeyboard[build.keyboardId]) buildsByKeyboard[build.keyboardId] = [];
	buildsByKeyboard[build.keyboardId].push(build.id);
}

// Plates (sorted by material name, ids updated)
plates['alu'] = new Plate('alu', 'Aluminum');
plates['cf'] = new Plate('cf', 'Carbon Fiber');
plates['fr4'] = new Plate('fr4', 'FR4');
plates['pc'] = new Plate('pc', 'Polycarbonate');
plates['pp'] = new Plate('pp', 'Polypropylene');
plates['pom'] = new Plate('pom', 'POM');



// Switches (sorted by switch name, ids updated)
switches['u4t'] = new Switch('u4t', 'Boba U4T', 'Tactile', 'Gazzew', null, false);
switches['mxblack'] = new Switch('mxblack', 'Cherry MX2A Black', 'Linear', 'Cherry', null, true);
switches['nixie'] = new Switch('nixie', 'Cherry Nixie', 'Linear', 'Cherry', null, true);
switches['doom'] = new Switch('doom', 'Doom Tactile', 'Tactile', 'Keebfront', null, false);
switches['milky_yellow'] = new Switch('milky_yellow', 'Gateron Milky Yellow', 'Linear', 'Gateron', null, false);
switches['smoothie'] = new Switch('smoothie', 'Gateron Smoothie', 'Linear', 'Gateron', null, false);
switches['caramel_latte'] = new Switch('caramel_latte', 'Huano Caramel Latte', 'Linear', 'Huano', null, true);
switches['cloud'] = new Switch('cloud', 'HMX Cloud', 'Linear', 'HMX', null, false);
switches['cheese'] = new Switch('cheese', 'HMX Cheese', 'Linear', 'HMX', null, true);
switches['hyacinth_v2'] = new Switch('hyacinth_v2', 'HMX Hyacinth V2', 'Linear', 'HMX', null, false);
switches['macchiato'] = new Switch('macchiato', 'HMX Macchiato', 'Linear', 'HMX', null, false);
switches['sugar_rush'] = new Switch('sugar_rush', 'HMX Sugar Rush', 'Linear', 'HMX', null, true);
switches['sunset_gleam'] = new Switch('sunset_gleam', 'HMX Sunset Gleam', 'Linear', 'HMX', null, false);
switches['xinhai'] = new Switch('xinhai', 'HMX Xinhai', 'Linear', 'HMX', null, true);
switches['yogurt'] = new Switch('yogurt', 'HMX Yogurt', 'Linear', 'HMX', null, true);
switches['purple_dawn'] = new Switch('purple_dawn', 'Keygeek Purple Dawn', 'Linear', 'Keygeek', null, false);
switches['uni'] = new Switch('uni', 'Keygeek Uni', 'Linear', 'Keygeek', null, false);
switches['y1x'] = new Switch('y1x', 'Keygeek Y1X', 'Linear', 'Keygeek', null, true);
switches['y3'] = new Switch('y3', 'Keygeek Y3', 'Linear', 'Keygeek', null, false);
switches['mz_z1'] = new Switch('mz_z1', 'MZ Z1', 'Linear', 'MZ', null, false);
switches['poro'] = new Switch('poro', 'HMX Poro', 'Linear', 'HMX', null, false);
switches['cosmic_purple'] = new Switch('cosmic_purple', 'HMX Cosmic Purple', 'Linear', 'HMX', null, false);
switches['lunar_stone'] = new Switch('lunar_stone', 'HMX Lunar Stone', 'Linear', 'HMX', null, true);
switches['yuying_linear'] = new Switch('yuying_linear', 'WingTree Yuying Linear', 'Linear', 'WingTree', 'wingtree', true);
switches['yuying_tactile'] = new Switch('yuying_tactile', 'WingTree Yuying Tactile', 'Tactile', 'WingTree', 'wingtree', true);
switches['yunie_linear'] = new Switch('yunie_linear', 'WingTree Yunie Linear', 'Linear', 'WingTree', 'wingtree', true);
switches['yunie_tactile'] = new Switch('yunie_tactile', 'WingTree Yunie Tactile', 'Tactile', 'WingTree', 'wingtree', true);
switches['y2'] = new Switch('y2', 'Keygeek Y2', 'Linear', 'Keygeek', null, false);
switches['khonsu'] = new Switch('khonsu', 'Gateron Khonsu', 'Linear', 'Gateron', null, false);
switches['orange_boi'] = new Switch('orange_boi', 'Keygeek Orange Boi', 'Linear', 'Keygeek', null, false);
switches['canglan_v3'] = new Switch('canglan_v3', 'HMX Canglan V3', 'Linear', 'HMX', null, false);
switches['jasmine_v2'] = new Switch('jasmine_v2', 'Keygeek Jasmine V2', 'Linear', 'Keygeek', null, false);
switches['serene_green'] = new Switch('serene_green', 'HMX Serene Green', 'Linear', 'HMX', null, false);
switches['b1'] = new Switch('b1', 'Keygeek B1', 'Linear', 'Keygeek', null, false);
switches['cloud_v2'] = new Switch('cloud_v2', 'HMX Cloud V2', 'Linear', 'HMX', null, false);
switches['explosion'] = new Switch('explosion', 'Keygeek Explosion', 'Linear', 'Keygeek', null, false);
switches['butterfly'] = new Switch('butterfly', 'Keygeek Butterfly', 'Linear', 'Keygeek', null, false);
switches['bad_sweetheart'] = new Switch('bad_sweetheart', 'HMX Bad Sweetheart', 'Linear', 'HMX', null, false);
switches['cream_latte'] = new Switch('cream_latte', 'HMX Cream Latte', 'Linear', 'HMX', null, false);



// Keycaps (sorted by name, ids updated)
keycaps['8008'] = new Keycap('8008', 'GMK 8008', 'GMK', 120, true, false);
keycaps['8008_bloo'] = new Keycap('8008_bloo', 'GMK 8008 Bloo', 'GMK', 120, true, false);
keycaps['blossom'] = new Keycap('blossom', 'GMK Blossom', 'GMK', 120, true, false);
keycaps['botanical'] = new Keycap('botanical', 'GMK Botanical', 'GMK', 120, true, true);
keycaps['darling'] = new Keycap('darling', 'GMK Darling', 'GMK', 120, false, true);
keycaps['digital_nightmares'] = new Keycap('digital_nightmares', 'GMK Digital Nightmares', 'GMK', 120, true, true);
keycaps['dracula_r2'] = new Keycap('dracula_r2', 'GMK Dracula R2', 'GMK', 120, true, true);
keycaps['fuji'] = new Keycap('fuji', 'GMK Fuji', 'GMK', 120, false, true);
keycaps['honor_dark'] = new Keycap('honor_dark', 'GMK Honor Dark', 'GMK', 120, false, false);
keycaps['mercury'] = new Keycap('mercury', 'GMK Mercury', 'GMK', 120, false, true);
keycaps['metropolis'] = new Keycap('metropolis', 'GMK Metropolis', 'GMK', 120, false, true);
keycaps['modern_materials_stone'] = new Keycap('modern_materials_stone', 'GMK Modern Materials Stone', 'GMK', 120, true, true);
keycaps['slasher'] = new Keycap('slasher', 'GMK Slasher', 'GMK', 120, false, true);
keycaps['tuzi'] = new Keycap('tuzi', 'GMK Tuzi', 'GMK', 120, false, true);
keycaps['fishing'] = new Keycap('fishing', 'JTK Fishing', 'JTK', 90, false, true);
keycaps['night_sakura'] = new Keycap('night_sakura', 'JTK Night Sakura', 'JTK', 90, false, true);
keycaps['skidata_neo'] = new Keycap('skidata_neo', 'KKB Skidata Neo', 'KKB', 90, true, true);
keycaps['bow'] = new Keycap('bow', 'KKB Black on White', 'KKB', 90, true, true);
keycaps['oblivion'] = new Keycap('oblivion', 'GMK Oblivion', 'GMK', 110, true, true);
keycaps['midnight'] = new Keycap('midnight', 'Domikey Midnight', 'DMK', 90, false, false); //alphas
keycaps['frost_witch'] = new Keycap('frost_witch', 'GMK Frost Witch', 'GMK', 110, true, true);


// Keyboards (sorted by name, ids updated)
keyboards['kb1'] = new Keyboard('kb1', 'BD60', 'BD', '60%', 229, 'Lilac');
keyboards['kb2'] = new Keyboard('kb2', 'Brutal65 V2', 'Brutal', '65%', 245, 'Gray');
keyboards['kb3'] = new Keyboard('kb3', 'Cycle8', 'TKD', '80%', 185, 'Zeal');
keyboards['kb4'] = new Keyboard('kb4', 'Isabell60', 'Isabell', '60%', 60, 'Gray');
keyboards['kb5'] = new Keyboard('kb5', 'Krush65', 'Nuxros', '65%', 170, 'PC');
keyboards['kb6'] = new Keyboard('kb6', 'Mode Encore', 'Mode', '65%', 309, 'White');
keyboards['kb7'] = new Keyboard('kb7', 'Mode Envoy', 'Mode', '65%', 264, 'PC', 'mode_envoy');
keyboards['kb8'] = new Keyboard('kb8', 'Mode Envoy', 'Mode', '65%', 239, 'White', 'mode_envoy');
keyboards['kb9'] = new Keyboard('kb9', 'Neo65', 'Neo', '65%', 119, 'Black');
keyboards['kb10'] = new Keyboard('kb10', 'Neo65 Cu', 'Neo', '65%', 210, 'Purple');
keyboards['kb11'] = new Keyboard('kb11', 'Neo70', 'Neo', '70%', 115, 'Navy');
keyboards['kb12'] = new Keyboard('kb12', 'Neo Ergo', 'Neo', 'Ergo', 163, 'Gray');
keyboards['kb13'] = new Keyboard('kb13', 'Neo Ergo', 'Neo', 'Ergo', 163, 'White');
keyboards['kb14'] = new Keyboard('kb14', 'Pavlov', 'KBDfans', '65%', 99, 'White', 'pavlov');
keyboards['kb15'] = new Keyboard('kb15', 'QK65', 'QwertyKeys', '65%', 160, 'Black');
keyboards['kb16'] = new Keyboard('kb16', 'DK1 TKL', 'GDK Labs', '80%', 200, 'Navy', 'dk1_tkl');

// Builds (with sound test fields)

Builds.forEach((b) => addBuild(new KeyboardBuild(
	b.id, b.name, b.keyboardId, b.switchId, b.keycapId, b.plateId, {
		soundDescriptors: b.soundDescriptors,
		audio: b.audio,
		youtube: b.youtube,
		hasSoundtest: b.hasSoundtest,
		thumbnail: b.thumbnail,
		foam: b.foam,
		mount: b.mount,
	})
));

export { Keyboard, Keycap, Switch, Plate, KeyboardBuild, Article };

export const articles = [];
Articles.forEach((a) => {
  articles.push(new Article(
    a.id,
    a.title,
    a.type,
    a.description,
    a.thumbnail,
    a.date,
    a.status,
    a.link,
    a.visible
  ));
});


